import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useParams } from 'react-router-dom';
import { Camera, X } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const EditTeacher = () => {
  const { api, user } = useAuth();
  const { teacherId } = useParams();
  const { t } = useTranslation('editteacher');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    email: '',
    photo: null,
    phone: '',
    id_number: '',
    date_of_joining: '',
    date_of_birth: '',
    tsc_number: '',
    address: ''
  });

  useEffect(() => {
    const fetchTeacher = async () => {
      try {
        const response = await api.get(`/api/v1/teachers/schools/${user.school_reg}/teachers/${teacherId}`);
        setFormData({
          ...response.data,
          date_of_birth: response.data.date_of_birth.split('T')[0],
          date_of_joining: response.data.date_of_joining.split('T')[0],
        });
        if (response.data.photo) {
          setPhotoPreview(response.data.photo);
        }
      } catch (err) {
        toast.error(t('alerts.loadFailure'), {
          duration: 3000,
          position: 'top-center',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTeacher();
  }, [teacherId, user.school_reg, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        photo: file
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setFormData(prev => ({
      ...prev,
      photo: null
    }));
    setPhotoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const loadingToast = toast.loading(t('alerts.updating'), {
      position: 'top-center'
    });

    try {
      const submissionData = { ...formData };
      // Removing photo from submission data
      delete submissionData.photo;

      await api.patch(
        `/api/v1/teachers/schools/${user.school_reg}/teachers/${teacherId}`,
        submissionData
      );
      
      toast.dismiss(loadingToast);
      toast.success(t('alerts.success'), {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(t('alerts.failure'), {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      });
    } finally {
      setSaving(false);
    }
  };

  const inputClassName = "w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors";
  const selectClassName = "w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors";
  const rtlClass = document.documentElement.dir === 'rtl' ? 'rtl text-right' : '';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        }}
      />
      <div className={`max-w-4xl mx-auto p-4 bg-white rounded-xl shadow-lg ${rtlClass}`}>
        <h2 className="text-3xl font-bold mb-8 text-gray-800 text-center">{t('pageTitle')}</h2>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Photo Upload Section */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              {photoPreview ? (
                <div className="relative w-32 h-32">
                  <img
                    src={photoPreview}
                    alt={t('photo.preview')}
                    className="w-full h-full object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 bg-gray-100 flex items-center justify-center rounded">
                  <Camera size={32} className="text-gray-400" />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>
            <div className="text-sm text-gray-600">
              {t('photo.uploadText')}
            </div>
          </div>

          {/* Personal Information Section */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-semibold mb-6 text-gray-800">{t('personal.sectionTitle')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('personal.fullName')}</label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('personal.fullNamePlaceholder')}
                  required
                  className={`${inputClassName} ${rtlClass}`}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('personal.gender')}</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  className={`${selectClassName} ${rtlClass}`}
                >
                  <option value="">{t('personal.genderSelect')}</option>
                  <option value="MALE">{t('personal.male')}</option>
                  <option value="FEMALE">{t('personal.female')}</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('personal.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('personal.emailPlaceholder')}
                  required
                  className={`${inputClassName} ${rtlClass}`}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('personal.phone')}</label>
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('personal.phonePlaceholder')}
                  required
                  className={`${inputClassName} ${rtlClass}`}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('personal.dateOfBirth')}</label>
                <div className="relative">
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    required
                    className={`${inputClassName} ${rtlClass}`}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('personal.address')}</label>
                <input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder={t('personal.addressPlaceholder')}
                  required
                  className={`${inputClassName} ${rtlClass}`}
                />
              </div>
            </div>
          </div>

          {/* Professional Information Section */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-semibold mb-6 text-gray-800">{t('professional.sectionTitle')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('professional.idNumber')}</label>
                <input
                  name="id_number"
                  value={formData.id_number || ''}
                  onChange={handleChange}
                  placeholder={t('professional.idNumberPlaceholder')}
                  className={`${inputClassName} ${rtlClass}`}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('professional.tscNumber')}</label>
                <input
                  name="tsc_number"
                  value={formData.tsc_number}
                  onChange={handleChange}
                  placeholder={t('professional.tscNumberPlaceholder')}
                  className={`${inputClassName} ${rtlClass}`}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('professional.dateOfJoining')}</label>
                <div className="relative">
                  <input
                    type="date"
                    name="date_of_joining"
                    value={formData.date_of_joining}
                    onChange={handleChange}
                    required
                    className={`${inputClassName} ${rtlClass}`}
                  />
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full p-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors shadow-md"
          >
            {saving ? t('button.loading') : t('button.submit')}
          </button>
        </form>
      </div>
    </>
  );
};

export default EditTeacher;